// Inventory route registry (ADR 027 — Response Contract).
//
// Every verb declares its grant and tenant source (ADR 062 — Access Grants,
// BL-1833 PR 6a): `apiHandler` authorizes before the handler runs. Cataloger
// holds both inventory grants, so reads and the create stay theirs.
import { responseType } from '@auctogon/core/api/registry';
import { defineAdminRoute } from './define-admin-route';
import { LOT_EDIT_MODAL_READ } from './lots';
// Type-only import (erased at build) — keeps the registry client-bundle-safe.
import type {
  InventoryDetailPayload,
  InventoryListPayload,
} from '../../schemas/responses/inventory-list';
import type { InventoryLotCreateResultDto } from '../../schemas/responses/inventory-lot-create';
import type { InventoryCountsPayload } from '../../schemas/responses/inventory-counts';
import type { InventoryFilterOptionsPayload } from '../../schemas/responses/inventory-filter-options';
import type { LotHistoryPayload } from '../../schemas/responses/lot-history';

/** The Inventory screen names its tenant as `?tenantId=` on every read. */
const LIST_TENANT = { from: 'input', field: 'tenantId', in: 'query' } as const;

/**
 * BL-2318: the lot-list reads an auction's Lots tab and the lot drawer's Lot
 * History make — the list, its counts and facets, the CSV export and the
 * history tab. They admit every role that can open a lot (`LOT_EDIT_MODAL_READ`),
 * so Accounting sees an auction's lots, not only Catalogers and Admins.
 * In-tenant staff reads are not sensitive (Michael Lawrie, 2026-09-24, BL-1876);
 * the writes keep `inventory.update`.
 */
const LOT_LIST_READ = LOT_EDIT_MODAL_READ;

export const inventory = {
  counts: defineAdminRoute({
      path: '/api/inventory/counts',
      params: {},
      // BL-946: filtered status-bucket counts, re-fetched on every filter
      // change. BL-1349 kept it that way — the call-once dropdown options moved
      // to `filter_options` below rather than merging into this filter-reactive
      // read (see inventory-filter-options.ts for the reasoning).
      methods: { GET: { grant: LOT_LIST_READ, tenant: LIST_TENANT, response: responseType<InventoryCountsPayload>() } },
    }),
  filter_options: defineAdminRoute({
      path: '/api/inventory/filter-options',
      params: {},
      // BL-1349: the call-once option lists for both lot-list screens' filter
      // bars. Replaces the deleted `/api/inventory/stats`, whose `stats` half
      // no client read and whose envelope was untyped and non-standard.
      methods: { GET: { grant: LOT_LIST_READ, tenant: LIST_TENANT, response: responseType<InventoryFilterOptionsPayload>() } },
    }),
  id: {
    index: defineAdminRoute({
        path: '/api/inventory/:id',
        params: { id: '' },
        // BL-950: the item PUT was removed — catalog editing writes the lot via
        // PATCH /api/lots/:id now (catalog inverted onto auction_lots). The item
        // is a grouping key. BL-1431 removed DELETE too: an inventory item has no
        // delete of its own — deleting its last lot is what deletes it (the
        // cascade in `archiveEmptiedInventoryItems`). GET is all that remains.
        // BL-1348 gave the detail its OWN declaration (`InventoryDetailPayload`)
        // — the list went lot-keyed, this stayed item-keyed; they share every
        // enrichment field through `InventoryRouteEnrichment`.
        //
        // BL-1833 PR 6a: `scoped`, not `record` — `:id` is an item id OR an
        // inventory number, and a number may resolve in any tenant where the
        // caller holds `inventory.read` (BUG-1750's `member-tenants` fallback,
        // with a 409 when it is ambiguous). The handler keeps `?tenantId=` as
        // the preferred tenant and refuses one outside the gate's set.
        methods: {
          GET: { grant: 'inventory.read', tenant: { from: 'scoped' }, response: responseType<InventoryDetailPayload>() },
        },
      }),
    // BL-1030: the read-only "Lot History" tab — every lot (in an auction) that
    // shares this inventory item. Slim static read; server-derived status labels.
    lots: defineAdminRoute({
        path: '/api/inventory/:id/lots',
        params: { id: '' },
        // `withDeleted`: deleting an item's last lot archives the item, and that
        // deleted lot's modal still opens this tab (ADR 018 — Soft Delete).
        methods: {
          GET: {
            grant: LOT_LIST_READ,
            tenant: { from: 'record', table: 'inventory_item', param: 'id', withDeleted: true },
            response: responseType<LotHistoryPayload>(),
          },
        },
      }),
  },
  // BL-1907: the lot-list CSV export (Inventory + an auction's Lots tab) — the
  // list's own read, unpaged and bounded. Gated on the list's READ grant, not
  // manage: exporting changes nothing (ADR 062 — Access Grants). The success
  // body is a file (text/csv), so the verb declares no response DTO; failures
  // keep the JSON envelope.
  export: defineAdminRoute({
      path: '/api/inventory/export',
      params: {},
      methods: {
        POST: { grant: LOT_LIST_READ, tenant: { from: 'input', field: 'tenantId', in: 'body' } },
      },
    }),
  index: defineAdminRoute({
      path: '/api/inventory',
      params: {},
      // Response DTO carrier (ADR 027): GET returns the tenant's inventory list
      // (InventoryListPayload); POST now mints a lot-centric composite create
      // (BL-942 — inventory_item + one unassociated auction_lots row in one
      // transaction) and returns InventoryLotCreateResultDto via respond().
      methods: {
        GET: { grant: LOT_LIST_READ, tenant: LIST_TENANT, response: responseType<InventoryListPayload>() },
        POST: {
          grant: 'inventory.update',
          tenant: { from: 'input', field: 'tenantId', in: 'body' },
          response: responseType<InventoryLotCreateResultDto>(),
        },
      },
    }),
};
